// Surprise Me (contract D.4.2, B.9). Header button (IconDice) plus a caret
// popover: era chips, a soundboard lock (only when features.sbd), and a
// Banger / Hidden gem toggle (only the kinds the band offers). The dice plays
// a whole show at once; the popover card shows the reason line, Reroll (the
// two alternates first, then a fresh pick) and Open show.
//
// The exclusion list is storage.getRecentSids (pushed by WEB-CORE as shows
// play) plus the picks made in this session. It is only ever sent as
// `exclude`; it is never rendered (contract D.6, H: no history UI).

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import * as api from '../api';
import { getRecentSids } from '../storage';
import { makeSource } from '../queue';
import { IconDice, IconChevronDown, IconRefresh, IconOpen, IconMic, IconStar } from '../icons';
import { resolveBand, featuresOf, erasOf, fmtDate, dateKeyFor, useDismiss, useUid, arrowNav } from './vibeUtil';
import { partBadge } from '../showPart';
import { FromTapeBadge, isFillBand, isFromTape, noVenue } from '../fromTape';
import './vibe.css';

const EXCLUDE_MAX = 50;
const KIND_LABELS = { banger: 'Banger', gem: 'Hidden gem' };
const KIND_HINTS = { banger: 'A show people love most', gem: 'A great show fewer people have heard' };

const initialPick = { loading: false, error: null, picks: [], idx: 0 };

// Optional (additive): claimPlayIntent() from App. Its token rides along to
// onPlaySegments, so a pick that lands after the listener chose something
// else is not played.
export default function SurpriseMeButton({ band, meta, onPlaySegments, onOpenShow, claimPlayIntent }) {
  const b = resolveBand(band);
  const bandKey = b ? b.key : null;
  const f = featuresOf(meta);
  const kinds = ['banger', 'gem'].filter((k) => f.surprise[k]);
  const randomOnly = kinds.length === 0; // WSP: uniform random, copy "Random pick"
  const eras = erasOf(meta);
  const name = randomOnly ? 'Random pick' : 'Surprise Me';

  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState('banger');
  const [era, setEra] = useState(null);
  const [sbd, setSbd] = useState(false);
  const [st, setSt] = useState(initialPick);

  const rootRef = useRef(null);
  const popRef = useRef(null);
  const caretRef = useRef(null);
  const shownRef = useRef([]); // sids picked this session: exclusion only, never rendered
  const reqRef = useRef(0);
  const openRef = useRef(open);
  openRef.current = open;
  const popId = useUid('vb-surprise');

  // new band: start clean
  useEffect(() => {
    reqRef.current += 1;
    shownRef.current = [];
    setSt(initialPick);
    setOpen(false);
    setEra(null);
    setSbd(false);
    setKind('banger');
  }, [bandKey]);

  useDismiss(
    open,
    (e) => {
      setOpen(false);
      if (e && e.type === 'keydown' && caretRef.current) caretRef.current.focus();
    },
    rootRef
  );

  // focus the popover when it is opened from the caret, so keyboard users land in it
  // Keep the popover inside the viewport (16px gutter) wherever the button
  // sits: it hangs from the button's right edge and shifts when that would
  // push it off either side.
  const [popPos, setPopPos] = useState(null);
  useLayoutEffect(() => {
    if (!open) return undefined;
    const place = () => {
      const root = rootRef.current;
      if (!root) return;
      const r = root.getBoundingClientRect();
      const vw = window.innerWidth || document.documentElement.clientWidth;
      const width = Math.min(330, vw - 32);
      let right = 0;
      if (r.right > vw - 16) right = r.right - (vw - 16);
      if (r.right - right - width < 16) right = r.right - width - 16;
      setPopPos({ width, right });
    };
    place();
    window.addEventListener('resize', place);
    return () => window.removeEventListener('resize', place);
  }, [open]);

  const focusPopRef = useRef(false);
  useEffect(() => {
    if (open && focusPopRef.current && popRef.current) popRef.current.focus();
    focusPopRef.current = false;
  }, [open]);

  if (!b || !meta || meta.mode === 'off') return null;

  const activeKind = kinds.indexOf(kind) >= 0 ? kind : kinds[0] || 'banger';

  const claim = () => (claimPlayIntent ? claimPlayIntent() : undefined);

  const play = (pick, intent) => {
    if (!pick || !pick.segment) return;
    onPlaySegments([pick.segment], makeSource('surprise', b), { title: name, intent });
  };

  const fetchPick = () => {
    if (st.loading) return;
    const my = ++reqRef.current;
    const intent = claim();
    setSt((s) => ({ ...s, loading: true, error: null }));
    const exclude = [];
    shownRef.current.concat(getRecentSids(b.key)).forEach((sid) => {
      if (exclude.length < EXCLUDE_MAX && exclude.indexOf(sid) < 0) exclude.push(sid);
    });
    api
      .surprise(b, {
        kind: randomOnly ? undefined : activeKind,
        era: era || undefined,
        sbd: f.sbd && sbd,
        exclude,
      })
      .then((res) => {
        if (my !== reqRef.current) return;
        const picks = [res && res.pick].concat((res && res.alternates) || []).filter((p) => p && p.segment && p.sid);
        if (!picks.length) {
          setSt({ ...initialPick, error: 'No shows match these settings.' });
          return;
        }
        shownRef.current = picks
          .map((p) => p.sid)
          .concat(shownRef.current.filter((sid) => !picks.some((p) => p.sid === sid)))
          .slice(0, EXCLUDE_MAX);
        setSt({ loading: false, error: null, picks, idx: 0 });
        play(picks[0], intent);
      })
      .catch((err) => {
        if (my !== reqRef.current) return;
        const msg = (err && err.userMessage) || 'Could not pick a show right now.';
        setSt((s) => ({ ...s, loading: false, error: msg }));
        if (!openRef.current) toast(msg, { type: toast.TYPE.ERROR });
      });
  };

  const onDice = () => {
    setOpen(true);
    if (!st.loading) fetchPick();
  };

  const reroll = () => {
    if (st.loading) return;
    if (st.idx < st.picks.length - 1) {
      const idx = st.idx + 1;
      setSt((s) => ({ ...s, idx, error: null }));
      play(st.picks[idx], claim());
    } else {
      fetchPick();
    }
  };

  // A settings change keeps the card on screen but drops the unplayed
  // alternates, so the next Reroll asks again with the new settings.
  const changed = (fn) => (v) => {
    fn(v);
    setSt((s) => (s.picks.length ? { ...s, picks: s.picks.slice(0, s.idx + 1) } : s));
  };
  const pickKind = changed(setKind);
  const pickEra = changed(setEra);
  const pickSbd = changed(setSbd);

  const pick = st.picks[st.idx] || null;
  const pickPart = partBadge(pick);
  const pickTape = isFillBand(meta) && isFromTape(pick);
  const tape = pick && pick.bestTape;
  const sbdTape = tape && (tape.sbd === 'sbd' || tape.sbd === 'mtx');

  const openShow = () => {
    if (!pick) return;
    setOpen(false);
    onOpenShow({
      bandKey: b.key,
      iso: pick.iso,
      dateKey: pick.dateKey || dateKeyFor(b.key, pick.iso),
      sid: pick.sid,
      tapeId: (tape && tape.id) || (pick.segment && pick.segment.tapeId) || null,
    });
  };

  return (
    <div className="vb-surprise vb-root" ref={rootRef}>
      <div className="vb-split" role="group" aria-label={name}>
        <button
          type="button"
          className={'icon-btn' + (open ? ' active' : '')}
          title={name}
          aria-label={name}
          onClick={onDice}
          aria-busy={st.loading}
        >
          {st.loading ? <span className="spinner vb-spin" aria-hidden="true" /> : <IconDice size={17} />}
        </button>
        <button
          type="button"
          ref={caretRef}
          className={'icon-btn vb-caret' + (open ? ' active' : '')}
          title={name + ' settings'}
          aria-label={name + ' settings'}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-controls={popId}
          onClick={() => {
            focusPopRef.current = !open;
            setOpen(!open);
          }}
        >
          <IconChevronDown size={12} />
        </button>
      </div>

      {open && (
        <div
          className="vb-pop"
          id={popId}
          role="dialog"
          aria-label={name}
          tabIndex={-1}
          ref={popRef}
          style={popPos ? { width: popPos.width, right: popPos.right } : undefined}
        >
          <div className="vb-pop-title">{name}</div>
          <div className="vb-pop-sub">
            {randomOnly ? 'Plays a random ' + b.name + ' show from the top.' : 'Picks a whole ' + b.name + ' show and plays it from the top.'}
          </div>

          {kinds.length > 1 && (
            <>
              <div className="vb-label">Pick</div>
              <div className="vb-seg" role="group" aria-label="Kind of show" onKeyDown={arrowNav}>
                {kinds.map((k) => (
                  <button key={k} type="button" aria-pressed={activeKind === k} title={KIND_HINTS[k]} onClick={() => pickKind(k)}>
                    {KIND_LABELS[k]}
                  </button>
                ))}
              </div>
              <div className="vb-note dim" style={{ marginTop: 6 }}>
                {KIND_HINTS[activeKind]}
              </div>
            </>
          )}

          {eras.length > 0 && (
            <>
              <div className="vb-label">Era</div>
              <div className="vb-chips" role="group" aria-label="Era" onKeyDown={arrowNav}>
                <button type="button" className="vb-chip" aria-pressed={!era} onClick={() => pickEra(null)}>
                  Any era
                </button>
                {eras.map((e) => (
                  <button key={e.key} type="button" className="vb-chip" aria-pressed={era === e.key} onClick={() => pickEra(e.key)}>
                    {e.label}
                  </button>
                ))}
              </div>
            </>
          )}

          {f.sbd && (
            <>
              <div className="vb-label">Sound</div>
              <button type="button" className="vb-chip" aria-pressed={sbd} onClick={() => pickSbd(!sbd)}>
                <IconMic size={12} /> Soundboard only
              </button>
            </>
          )}

          {!pick && (
            <div className="vb-pop-go">
              <button type="button" className="vb-btn primary" onClick={fetchPick} aria-busy={st.loading}>
                <IconDice size={14} /> Pick a show
              </button>
              {st.loading && <span className="spinner vb-spin" aria-hidden="true" />}
            </div>
          )}

          <div aria-live="polite">
            {pick && (
              <div className="vb-card">
                <div className="vb-card-top">
                  <div className="row-main">
                    <div className="vb-card-date">
                      {fmtDate(pick.iso)}
                      {pickPart && (
                        <span className="badge vb-part" title={pickPart.title}>
                          {pickPart.text}
                        </span>
                      )}
                      {pickTape && <FromTapeBadge row={pick} className="vb-part vb-fromtape" />}
                    </div>
                    <div className="vb-card-venue">
                      {pickTape && noVenue(pick.venue) ? (noVenue(pick.location) ? 'Venue unknown' : 'Venue unknown, ' + pick.location) : pick.venue}
                      {!(pickTape && noVenue(pick.venue)) && pick.location ? ', ' + pick.location : ''}
                    </div>
                  </div>
                  {sbdTape && (
                    <span className="sbd-badge" title="Soundboard recording">
                      SBD
                    </span>
                  )}
                  {tape && typeof tape.rating10 === 'number' && (
                    <span className="rating" title={'Rated ' + tape.rating10 + ' of 10'}>
                      <IconStar size={12} /> {tape.rating10}
                    </span>
                  )}
                </div>
                {pick.reason && <div className="vb-reason">{pick.reason}</div>}
                <div className="vb-card-actions">
                  <button type="button" className="vb-btn" onClick={reroll} aria-busy={st.loading} title="Play a different show">
                    {st.loading ? <span className="spinner vb-spin" aria-hidden="true" /> : <IconRefresh size={14} />} Reroll
                  </button>
                  <button type="button" className="vb-btn" onClick={openShow} title="Open the full show">
                    <IconOpen size={14} /> Open show
                  </button>
                </div>
              </div>
            )}
            {st.error && !st.loading && <div className="vb-error">{st.error}</div>}
          </div>
        </div>
      )}
    </div>
  );
}
